import React from "react";
import { Repeat, X, CheckCircle2, XCircle, Clock } from "lucide-react";
import { formatDisplayDate } from "../utils/dateUtils";
import { getOccurrenceHistory, weekdayShortList } from "../utils/recurrence";

/**
 * Shows the occurrence history of a recurring Remember item: every occurrence
 * from its creation date up to today (each Completed / Missed / Today), plus a
 * summary of completed, missed and upcoming counts until the end date.
 */
const RecurrenceHistoryModal = ({ isOpen, item, onClose }) => {
  if (!isOpen || !item) return null;

  const { entries, completed, missed, upcoming } = getOccurrenceHistory(item);
  const weekdays = weekdayShortList(item.recurrence?.weekdays || []);

  const statusMeta = {
    completed: {
      icon: <CheckCircle2 className="w-4 h-4 text-green-400" />,
      label: "Completed",
      cls: "text-green-300",
    },
    missed: {
      icon: <XCircle className="w-4 h-4 text-red-400" />,
      label: "Missed",
      cls: "text-red-300",
    },
    today: {
      icon: <Clock className="w-4 h-4 text-purple-300" />,
      label: "Today",
      cls: "text-purple-200",
    },
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-3xl p-6 max-w-md w-full border-2 border-purple-500/40 shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Repeat className="w-5 h-5 text-purple-400 flex-shrink-0" />
            <h3 className="text-lg font-bold text-purple-300 break-words">
              {item.text}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="text-xs text-purple-200/80 mb-3 flex flex-wrap items-center gap-2">
          <span className="flex gap-1">
            {weekdays.map((d) => (
              <span
                key={d}
                className="bg-purple-500/20 text-purple-200 px-1.5 py-0.5 rounded"
              >
                {d}
              </span>
            ))}
          </span>
          {item.recurrence?.endDate && (
            <span className="text-gray-400">
              until {formatDisplayDate(new Date(item.recurrence.endDate))}
            </span>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-700/60 rounded-xl p-2 text-center">
            <div className="text-green-400 font-bold text-lg">{completed}</div>
            <div className="text-[11px] text-gray-400">Completed</div>
          </div>
          <div className="bg-gray-700/60 rounded-xl p-2 text-center">
            <div className="text-red-400 font-bold text-lg">{missed}</div>
            <div className="text-[11px] text-gray-400">Missed</div>
          </div>
          <div className="bg-gray-700/60 rounded-xl p-2 text-center">
            <div className="text-purple-300 font-bold text-lg">{upcoming}</div>
            <div className="text-[11px] text-gray-400">Upcoming</div>
          </div>
        </div>

        {/* Occurrence list (creation -> today) */}
        <div className="overflow-y-auto flex-1 min-h-0 space-y-1 pr-1">
          {entries.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-4">
              No occurrences yet. The first one is still upcoming.
            </p>
          ) : (
            entries
              .slice()
              .reverse()
              .map((e) => {
                const meta = statusMeta[e.status] || statusMeta.today;
                return (
                  <div
                    key={e.date}
                    className="flex items-center justify-between gap-2 bg-gray-700/40 rounded-lg px-3 py-1.5"
                  >
                    <span className="flex items-center gap-2 text-sm text-cyan-200">
                      {meta.icon}
                      {formatDisplayDate(new Date(e.date))}
                    </span>
                    <span className={`text-xs font-medium ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
};

export default RecurrenceHistoryModal;
